import React from "react"

export interface BodyStylesProps {
  /**
   * Additional classes to add to the wrapper element
   */
  className?: string
  /**
   * Content to style
   */
  children: React.ReactNode
  /**
   * Is this on a dark background where dark text would be difficult to read?
   */
  contrast?: boolean
  /**
   * - **light** – the default, this style uses large areas of white with bold cyan and dark gray as secondary colors.
   * - **gray** – lighter text, often used on shorter blocks of content in layouts that are more visually interesting. Should be avoided for long form content
   * - **dark** – adds orange accents, often used when the photography and subject matter dictate a dark, solid feel, such as bold industrial-style imagery
   */
  colorScheme?: "light" | "gray" | "dark"
  /**
   * - **xl** – increases the base font size for elements like paragraphs and lists, often used on shorter blocks of text on pages where content is sparse
   * - **`undefined`** – use the base font size for elements like paragraphs and lists
   */
  textSize?: "xl"
  /**
   * - **article** – transforms `h3` headings to uppercase and adds cyan headings for `h3` headings and gray headings for `h4` headings.
   * - **compact** – reduces heading sizes and space between elements. Typically used in modals or switchers where the base text sizes and spacing doesn’t look right
   * - **landingPage** – bolds items in lists so they are more prominent on the page
   */
  theme?: "base" | "article" | "compact" | "landingPage"
}

/**
 *
 * Adds styles to HTML from rich text fields
 */
const BodyStyles = ({
  className,
  children,
  contrast,
  colorScheme,
  textSize,
  theme,
}: BodyStylesProps) => {
  return (
    <div
      className={`
      prose
      ${
        {
          true: "prose-contrast",
        }[contrast] || ``
      }
      ${
        {
          gray: "prose-gray",
          dark: "prose-dark",
        }[colorScheme] || ``
      }
      ${
        {
          xl: "prose-xl",
        }[textSize] || ``
      }
      ${
        {
          article: "prose-article",
          compact: "prose-compact",
          landingPage: "prose-landing-page",
        }[theme] || ``
      }
      ${className}
    `}
    >
      {children}
    </div>
  )
}

export default BodyStyles
